<script lang="ts" setup>
import type { ForeignMachineryRentalPriceDefinition } from '~/types'

export interface ForeignMachineryRentalPriceDefinitionInput {
  pricePerDay: number
  daysLte: number
}

const props = defineProps<{ payload: ForeignMachineryRentalPriceDefinitionInput[] }>()
defineEmits<{
  (e: 'submit', data: ForeignMachineryRentalPriceDefinition[]): void
  (e: 'close'): void
}>()

const { cloned: priceDefinitions } = useCloned<ForeignMachineryRentalPriceDefinitionInput[]>(props.payload)

const computedPriceDefinitions = computed<ForeignMachineryRentalPriceDefinition[]>(() => priceDefinitions.value.map((price, index, prices) => {
  let daysGte = 1
  const isLastPrice = index === (prices.length - 1)

  const previousPrice = prices[index - 1]
  if (previousPrice && typeof previousPrice.daysLte === 'number') {
    daysGte = previousPrice.daysLte + 1
  }

  return { daysGte, daysLte: isLastPrice ? undefined : price.daysLte, pricePerDay: price.pricePerDay }
}))

const isInputValid = computed(() => computedPriceDefinitions.value.every((price, index, array) => {
  if (typeof price.daysLte === 'number' && price.daysLte < price.daysGte) {
    return false
  }
  if (index === 0) {
    return true
  }
  // Verify that the previous price has daysLte set and that the previous day range does not overlap with the current one
  const priorPrice = array[index - 1]
  if (typeof priorPrice.daysLte !== 'number' || priorPrice.daysLte >= price.daysGte) {
    return false
  }
  return true
}))

function addPriceDefinition() {
  let daysLte = 1

  const lastPrice = priceDefinitions.value.at(-1)
  if (lastPrice) {
    daysLte = lastPrice.daysLte + 1
  }

  priceDefinitions.value.push({ pricePerDay: 0, daysLte })
}

function removeLastPriceDefinition() {
  priceDefinitions.value = priceDefinitions.value.slice(0, -1)
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-for="(price, index) in priceDefinitions" :key="index" class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-gray-50 p-2.5 rounded">
      <div class="flex items-end gap-2">
        <label>
          <span class="text-base font-semibold">
            <template v-if="index === (priceDefinitions.length - 1)">
              {{ $t('customerModule.machinery.manage.rentalPrice.from') }}
            </template>
            <template v-else>
              {{ $t('customerModule.machinery.manage.rentalPrice.fromTo') }}
            </template>
          </span>
          <p class="text-lg bg-white rounded py-1 pl-3 pr-5 border min-w-36">
            {{ computedPriceDefinitions[index].daysGte }} {{ $t('customerModule.machinery.manage.rentalPrice.days') }}
          </p>
        </label>

        <div v-if="index !== (priceDefinitions.length - 1)">
          <span class="text-base font-semibold">
            {{ $t('customerModule.machinery.manage.rentalPrice.to') }}
          </span>
          <n-input-group>
            <n-button size="large" @click="price.daysLte = price.daysLte - 1">
              <template #icon>
                <Icon name="material-symbols:remove-rounded" />
              </template>
            </n-button>
            <n-input-number
              v-model:value="price.daysLte"
              size="large"
              :min="computedPriceDefinitions[index].daysGte"
              :status="!price.daysLte ? 'error' : undefined"
              :show-button="false"
            >
              <template #suffix>
                {{ $t('customerModule.machinery.manage.rentalPrice.days') }}
              </template>
            </n-input-number>
            <n-button size="large" @click="price.daysLte = price.daysLte + 1">
              <template #icon>
                <Icon name="material-symbols:add-rounded" />
              </template>
            </n-button>
          </n-input-group>
        </div>

        <n-button v-else-if="index !== 0" size="large" type="error" secondary @click="removeLastPriceDefinition">
          <template #icon>
            <Icon name="material-symbols:delete-outline-rounded" />
          </template>
        </n-button>
      </div>

      <div class="flex items-end gap-2">
        <label>
          <span class="text-base font-semibold">
            {{ $t('customerModule.machinery.manage.rentalPrice.price') }}
          </span>
          <n-input-number v-model:value="price.pricePerDay" size="large">
            <template #suffix>
              €
            </template>
          </n-input-number>
        </label>
      </div>
    </div>

    <n-button
      type="primary"
      secondary
      @click="addPriceDefinition"
    >
      <template #icon>
        <Icon name="material-symbols:add-box-outline-rounded" />
      </template>
      {{ $t('customerModule.machinery.manage.rentalPrice.add') }}
    </n-button>

    <n-divider />

    <div class="flex items-center gap-2">
      <n-button type="primary" size="large" :disabled="!isInputValid" class="md:min-w-32" @click="$emit('submit', computedPriceDefinitions)">
        {{ $t('button.save') }}
      </n-button>

      <n-button size="large" class="min-w-32" @click="$emit('close')">
        {{ $t('customerModule.button.cancel') }}
      </n-button>
    </div>
  </div>
</template>
